/**
 * PreviewFunnel — the "Result" panel of Combine & Filter (prototype v39
 * `#page-combine-filter`, `renderCombineFunnel` / `combineDupesTable`).
 *
 * UI-parity pass (2026-09-23, orchestrator addendum), superseding this
 * component's own earlier iter-1 fix (finding M2) on ONE point: tone. M2
 * fixed the ORDER (compute order, not display order) and picked "Not in
 * every list" / "Filtered out" as AUTOMATIC (grey) because they are
 * deterministic given the recipe. The mock's `renderCombineFunnel` colours
 * them 'user' (accent) instead — the deterministic COMPUTATION is automatic,
 * but the row only exists at all because the recruiter turned that filter
 * on, which is the "your choice" the accent colour is for. `Duplicates
 * merged` and the new `Left out in source` are the only two rows that can
 * appear with NO recruiter action at all, so they stay grey. This file
 * follows the mock's tone, not M2's.
 *
 * Round 2 (sub-task R2-B) re-tunes tone AGAIN, on the SAME axis: "your
 * choices" rows move from accent to the WARNING tokens (orange), matching
 * the mock comparison's cosmetics pass. Accent stays reserved for the single
 * big result number below. `in_thrive` (the new "already in Thrive" step)
 * joins the your-choices set — it only ever narrows the result because the
 * recruiter left "Remove contacts already in Thrive" on.
 *
 * Rows, in COMPUTE order (also the RENDER order now — this pass also fixes
 * a pre-existing mismatch where "Duplicates merged" rendered ahead of "Left
 * out in source" despite this file's own docstring saying otherwise), each
 * conditionally shown:
 *   Contacts in lists (always, no sign) · Left out in source (automatic,
 *   only when > 0) · Duplicates merged (automatic, always) · Not in every
 *   list / In fewer than N lists (your choice, only while "In at least N
 *   lists" is on; the first label at N = every list) · Already in
 *   search / Already in Thrive (your choice, only while `removeInThrive` is
 *   not `"off"`; label follows the mode) · Filtered out (your choice, only
 *   while a filter has values) · Removed by you (your choice, only when > 0)
 *   · an INFORMATIONAL "Not checked in Thrive N" line (no minus — it does not
 *   subtract from the result; N is people IN the result whose lists are not
 *   compared, so never more than the result; only when `not_checked > 0`)
 *   · a divider, then
 *   the big accent total + the target search pill.
 *
 * No "≈" anywhere: every count here is exact (Pattern 2, computed
 * synchronously), never an estimate.
 *
 * Duplicates drill-down (`combineDupesTable`): the "Duplicates merged"
 * row's value becomes a dashed-underline button when the response carries
 * `duplicate_groups` (an optional field a matching backend fix adds — see
 * `imports.client.ts`'s `CombineDuplicateGroup`; ABSENT entirely on an
 * older/stale response, in which case the row renders as a plain,
 * non-interactive number). Expands a table: Name (+title), LinkedIn URL,
 * "Found in" pills per list — the kept list ✓ in accent, the others
 * struck through, source titles run through `displayTitle` so a ".csv"
 * extension never leaks into the pill (round 2 cosmetics). "Showing K of N"
 * when `duplicate_groups_total` exceeds the page this response carried.
 */
import { useState } from "react";

import { displayTitle } from "../../import-detail/importTitle";
import { InfoTip } from "../InfoTip";

import type { CombineDuplicateGroup, CombineFunnel } from "../../../../clients/imports.client";

export interface PreviewFunnelProps {
  funnel: CombineFunnel;
  /** "In at least N lists" as applied — the N, or `null` when the rule is
   *  off. The page passes it already gated on 2+ sources (with one source
   *  the rule is a no-op, so there is no line to show). The row counts
   *  `funnel.not_in_enough_lists`. */
  minSources: number | null;
  /** How many lists are selected: N equal to it reads "Not in every list". */
  sourceCount: number;
  /** Any filter that counts in `filtered_out` is on. */
  filtersActive: boolean;
  /** "Remove contacts already in Thrive" (Filters card). The `in_thrive` row
   *  shows only while this is not `"off"`, labelled by which scope is active. */
  removeInThrive: "off" | "search" | "any";
  duplicateGroups?: readonly CombineDuplicateGroup[];
  duplicateGroupsTotal?: number;
  targetSearchId?: string | null;
  targetSearchName?: string | null;
  /** Scrolls to / focuses the People-lists card's Compare with Thrive
   *  status (each list's progress, or why it is not checked) — the
   *  `not_checked` line's own click-through. Omitted renders a plain
   *  (non-interactive) informational line. */
  onGoToCompare?: () => void;
}

type FunnelTone = "total" | "automatic" | "manual";

interface FunnelRow {
  label: string;
  value: number;
  testId: string;
  tone: FunnelTone;
}

function labelClass(tone: FunnelTone): string {
  if (tone === "manual") return "text-body text-warning-ink";
  return "text-body text-text-secondary";
}

function valueClass(tone: FunnelTone): string {
  if (tone === "manual") return "text-body text-warning-ink";
  if (tone === "automatic") return "text-body text-text-muted";
  return "text-body text-text";
}

function linkedinHref(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function DuplicatesTable({
  groups,
  total,
}: {
  groups: readonly CombineDuplicateGroup[];
  total: number;
}) {
  return (
    <div
      data-testid="combine-funnel-duplicates-table"
      className="rounded-md border border-border bg-bg p-2"
    >
      <table className="w-full border-collapse text-caption">
        <thead>
          <tr className="border-b border-border text-left text-text-secondary">
            <th className="py-1">Name</th>
            <th className="py-1">LinkedIn</th>
            <th className="py-1">Found in</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr key={g.linkedin_url} className="border-b border-border last:border-b-0">
              <td className="py-1 align-top">
                <div className="text-text">{g.name}</div>
                {g.title ? <div className="text-text-muted">{g.title}</div> : null}
              </td>
              <td className="py-1 align-top">
                <a
                  href={linkedinHref(g.linkedin_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:text-accent-hover hover:underline"
                >
                  {g.linkedin_url}
                </a>
              </td>
              <td className="py-1 align-top">
                <div className="flex flex-wrap gap-1">
                  {g.lists.map((l) => (
                    <span
                      key={l.import_id}
                      data-testid={`combine-funnel-duplicates-pill-${g.linkedin_url}-${l.import_id}`}
                      className={
                        l.kept
                          ? "rounded-pill border border-accent bg-accent-muted px-1.5 py-0.5 text-accent"
                          : "rounded-pill border border-border px-1.5 py-0.5 text-text-muted line-through"
                      }
                    >
                      {l.kept ? "✓ " : ""}
                      {displayTitle(l.title)}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {groups.length < total ? (
        <p
          className="mt-1 text-caption text-text-muted"
          data-testid="combine-funnel-duplicates-more"
        >
          Showing {groups.length.toLocaleString()} of {total.toLocaleString()}
        </p>
      ) : null}
    </div>
  );
}

/** "Already in search" / "Already in Thrive" — label by mode, per the plan's
 *  own "your choice" wording. */
function inThriveLabel(mode: "search" | "any"): string {
  return mode === "search" ? "Already in search" : "Already in Thrive";
}

export function PreviewFunnel({
  funnel,
  minSources,
  sourceCount,
  filtersActive,
  removeInThrive,
  duplicateGroups,
  duplicateGroupsTotal,
  targetSearchId,
  targetSearchName,
  onGoToCompare,
}: PreviewFunnelProps) {
  const [dupesOpen, setDupesOpen] = useState(false);

  // Rows BEFORE "Duplicates merged" in compute order — just this one today,
  // split out (rather than folded into `afterDupRows`) so the render below
  // can place the duplicates block between the two halves without a special
  // case per row.
  const beforeDupRows: FunnelRow[] = [];
  if (funnel.left_out_in_source !== undefined && funnel.left_out_in_source > 0) {
    beforeDupRows.push({
      label: "Left out in source",
      value: funnel.left_out_in_source,
      testId: "combine-funnel-left-out",
      tone: "automatic",
    });
  }

  const afterDupRows: FunnelRow[] = [];
  if (minSources !== null) {
    afterDupRows.push({
      label: minSources >= sourceCount ? "Not in every list" : `In fewer than ${minSources} lists`,
      value: funnel.not_in_enough_lists,
      testId: "combine-funnel-not-every",
      tone: "manual",
    });
  }
  if (removeInThrive !== "off") {
    afterDupRows.push({
      label: inThriveLabel(removeInThrive),
      value: funnel.in_thrive,
      testId: "combine-funnel-in-thrive",
      tone: "manual",
    });
  }
  if (filtersActive) {
    afterDupRows.push({
      label: "Filtered out",
      value: funnel.filtered_out,
      testId: "combine-funnel-filtered",
      tone: "manual",
    });
  }
  if (funnel.excluded > 0) {
    afterDupRows.push({
      label: "Removed by you",
      value: funnel.excluded,
      testId: "combine-funnel-excluded",
      tone: "manual",
    });
  }

  const showDuplicatesDrilldown = Boolean(duplicateGroups?.length) && funnel.duplicates_merged > 0;

  const searchPill =
    targetSearchId != null ? (
      <span className="ml-auto flex items-center gap-1.5 text-caption text-text-secondary">
        <span aria-hidden="true">→</span>
        <span className="h-2 w-2 rounded-full bg-accent" aria-hidden="true" />
        {targetSearchId}
        {targetSearchName ? ` · ${targetSearchName}` : ""}
      </span>
    ) : null;

  return (
    <dl data-testid="combine-preview-funnel" className="flex flex-col gap-1.5">
      {/* Contacts in lists — always first, no sign (COMPUTE order start). */}
      <div data-testid="combine-funnel-total" className="flex items-center justify-between gap-3">
        <dt className={labelClass("total")}>Contacts in lists</dt>
        <dd className={`${valueClass("total")} tabular-nums`}>
          {funnel.total_rows.toLocaleString()}
        </dd>
      </div>

      {beforeDupRows.map((row) => (
        <div
          key={row.testId}
          data-testid={row.testId}
          className="flex items-center justify-between gap-3"
        >
          <dt className={labelClass(row.tone)}>{row.label}</dt>
          <dd className={`${valueClass(row.tone)} tabular-nums`}>− {row.value.toLocaleString()}</dd>
        </div>
      ))}

      {/* Duplicates merged — third in compute order (after "Left out in
          source"), with the optional drill-down. Rendered by hand rather
          than folded into the row arrays, since it is the only row with a
          click affordance. */}
      <div
        data-testid="combine-funnel-duplicates"
        className="flex items-center justify-between gap-3"
      >
        <dt className={labelClass("automatic")}>Duplicates merged</dt>
        {showDuplicatesDrilldown ? (
          <button
            type="button"
            onClick={() => setDupesOpen((v) => !v)}
            aria-expanded={dupesOpen}
            data-testid="combine-funnel-duplicates-toggle"
            className="border-b border-dashed border-text-muted text-body text-text-muted tabular-nums hover:text-text"
          >
            − {funnel.duplicates_merged.toLocaleString()}
          </button>
        ) : (
          <dd className={`${valueClass("automatic")} tabular-nums`}>
            − {funnel.duplicates_merged.toLocaleString()}
          </dd>
        )}
      </div>
      {dupesOpen && duplicateGroups?.length ? (
        <DuplicatesTable
          groups={duplicateGroups}
          total={duplicateGroupsTotal ?? duplicateGroups.length}
        />
      ) : null}

      {afterDupRows.map((row) => (
        <div
          key={row.testId}
          data-testid={row.testId}
          className="flex items-center justify-between gap-3"
        >
          <dt className={labelClass(row.tone)}>{row.label}</dt>
          <dd className={`${valueClass(row.tone)} tabular-nums`}>− {row.value.toLocaleString()}</dd>
        </div>
      ))}

      {/* "Not checked in Thrive N" — INFORMATIONAL, no minus sign: it does
          not subtract from `result` (see `CombineFunnel.not_checked`'s own
          docstring). The ⓘ explains why, as a tooltip and as its accessible
          name (PR #962 follow-up F4: the reason used to be `sr-only`, so
          sighted users never saw it), and the count is a button —
          when `onGoToCompare` is supplied — that scrolls to the People-lists
          card's Compare with Thrive status. */}
      {funnel.not_checked > 0 ? (
        <div
          data-testid="combine-funnel-not-checked"
          className="flex items-center justify-between gap-3"
        >
          <dt className="flex items-center gap-1.5 text-body text-text-secondary">
            Not checked in Thrive
            <InfoTip content="Their list has no Compare with Thrive that counts for this mode, or that Compare did not look them up." />
          </dt>
          {onGoToCompare ? (
            <button
              type="button"
              onClick={onGoToCompare}
              data-testid="combine-funnel-not-checked-action"
              className="border-b border-dashed border-text-muted text-body text-text-muted tabular-nums hover:text-text"
            >
              {funnel.not_checked.toLocaleString()}
            </button>
          ) : (
            <dd className="text-body text-text-muted tabular-nums">
              {funnel.not_checked.toLocaleString()}
            </dd>
          )}
        </div>
      ) : null}

      <div
        data-testid="combine-funnel-result"
        className="mt-1 flex items-center gap-3 border-t border-border pt-2"
      >
        {/* Bigger than before (round 2 cosmetics) — was `text-h3`. */}
        <dd className="text-display font-semibold text-accent tabular-nums">
          {funnel.result.toLocaleString()}
        </dd>
        <dt className="text-body text-text-secondary">contacts</dt>
        {searchPill}
      </div>
    </dl>
  );
}
